import { t } from 'i18next';
import { DEVICE_LOCK_STATES } from 'vio-constants';
import { areArraysEqual } from 'vio-libs';
export function calculateLockerDoorLocks(lockers, lockerSKU, devices) {
    const filteredLockers = lockers.filter(locker => {
        const { sku: leftLocker } = locker;
        const rightLocker = leftLocker.replace('B', 'A');
        return leftLocker === lockerSKU || rightLocker === lockerSKU;
    });
    if (filteredLockers.length < 1 || !devices || devices.length < 1) {
        return undefined;
    }
    const lockerDoorLocks = {};
    for (let i = 0; i < filteredLockers.length; i += 1) {
        const { name, sku } = filteredLockers[i];
        const [lockerPosition, lockerLocation] = sku.split('.');
        const lockDevice = devices.find(device => {
            const { position, location } = device.metadata;
            return position.toUpperCase() === lockerPosition && location.toUpperCase() === lockerLocation;
        });
        if (lockDevice) {
            const { id, status } = lockDevice;
            const lockStatus = status && status[0] && status[0].value;
            lockerDoorLocks[sku] = {
                id,
                lockStatus,
                name,
                sku
            };
        }
        else {
            lockerDoorLocks[sku] = null;
        }
    }
    return lockerDoorLocks;
}
export function calculateLockerLockName(lock, isFrontRearLocker = false) {
    if (!lock) {
        return undefined;
    }
    const { name, sku } = lock;
    if (!sku || sku.length < 1) {
        return undefined;
    }
    const [positionId, rack] = sku.split('.');
    if (positionId && rack) {
        const lockerPosition = calculateLockerPosition(positionId);
        const rackId = rack.charAt(1);
        if (lockerPosition !== 'Facility') {
            const lockerType = lockerPosition.charAt(lockerPosition.length - 1);
            const position = lockerPosition.substring(0, lockerPosition.length - 1);
            const lockerName = name ? name : position;
            let door;
            if (isFrontRearLocker) {
                door = lockerType === 'A' ? t('FRONT_DOOR', {
                    ns: 'Common'
                }) : t('REAR_DOOR', {
                    ns: 'Common'
                });
            }
            else {
                door = lockerType === 'A' ? t('LEFT_DOOR', {
                    ns: 'Common'
                }) : t('RIGHT_DOOR', {
                    ns: 'Common'
                });
            }
            return `${door}, ${lockerName}, ${t('RACK', {
                ns: 'Common'
            })} ${rackId}`;
        }
        return `${lockerPosition} ${t('DOOR', {
            ns: 'Common'
        })}, ${t('RACK', {
            ns: 'Common'
        })} ${rackId}`;
    }
    return undefined;
}
export function calculateLockerName(locker) {
    if (!locker || locker.length < 1) {
        return undefined;
    }
    const [positionId, rack] = locker.split('.');
    if (positionId && rack) {
        const lockerPosition = calculateLockerPosition(positionId);
        const locker = lockerPosition.replace(/[AB]/, '');
        const rackId = rack.charAt(1);
        const lockerName = `${locker} (${t('RACK', {
            ns: 'Common'
        })} ${rackId})`;
        return lockerName;
    }
    return undefined;
}
export function calculateLockerPosition(positionId) {
    if (!positionId || positionId.length === 0) {
        return undefined;
    }
    if (positionId.charAt(positionId.length - 1) === 'F') {
        return 'Facility';
    }
    return `Level ${positionId.substring(1)}`;
}
export function getLockers(lockers, status) {
    return lockers.filter(locker => {
        return locker.status === status;
    });
}
export function getLockStatus(lockStatus) {
    const { LOCKED, LOCKED_ELECTRICALLY, LOCKED_MECHANICALLY, UNLOCKED, UNLOCKED_ELECTRICALLY, UNLOCKED_MECHANICALLY, UNSECURED } = DEVICE_LOCK_STATES;
    switch (lockStatus) {
        case LOCKED:
        case LOCKED_ELECTRICALLY:
        case LOCKED_MECHANICALLY:
            return t('LOCKED', {
                ns: 'Common'
            });
        case UNSECURED:
            return t('UNSECURED', {
                ns: 'Common'
            });
        case UNLOCKED:
        case UNLOCKED_ELECTRICALLY:
        case UNLOCKED_MECHANICALLY:
            return t('UNLOCKED', {
                ns: 'Common'
            });
        default:
            return undefined;
    }
}
export function getRackByLocker(locker) {
    const sku = locker.split('.');
    const rackId = sku[1];
    return rackId.charAt(1);
}
export function getSiteIdByLocker(locker) {
    const sku = locker.split('.');
    const lastIndex = sku.length - 1;
    return sku[lastIndex];
}
export function calculateSiteLockersTableData(lockers, crossConnections = [], networkInterfaces = []) {
    const lockerCrossConnections = {};
    const lockerNetworkInterfaces = {};
    if (!lockers) {
        return;
    }
    const filteredLockers = lockers.filter(locker => {
        const { status } = locker;
        return status !== 'Networking' && status !== 'Facility';
    });
    for (let i = 0; i < crossConnections.length; i += 1) {
        const { ports } = crossConnections[i];
        const connectionPorts = ports.reduce((allPorts, currPort) => {
            const { uri } = currPort.port;
            allPorts.push(uri);
            return allPorts.sort();
        }, []);
        for (let j = 0; j < ports.length; j += 1) {
            const { port } = ports[j];
            const { uri } = port;
            const separatorIndex = uri.indexOf('.');
            const lockerName = uri.slice(separatorIndex + 1);
            const lockerSKU = lockerName.replace('B', 'A');
            if (lockerSKU in lockerCrossConnections) {
                const vccs = lockerCrossConnections[lockerSKU];
                for (let k = 0; k < vccs.length; k += 1) {
                    const vcc = vccs[k];
                    if (areArraysEqual(connectionPorts, vcc)) {
                        break;
                    }
                    if (k === vccs.length - 1) {
                        lockerCrossConnections[lockerSKU].push(connectionPorts);
                    }
                }
            }
            else {
                lockerCrossConnections[lockerSKU] = [connectionPorts];
            }
        }
    }
    for (let i = 0; i < networkInterfaces.length; i += 1) {
        const { device_id: deviceID, name } = networkInterfaces[i];
        const lockerSKU = deviceID.replace('B', 'A');
        if (lockerSKU in lockerNetworkInterfaces) {
            lockerNetworkInterfaces[lockerSKU].push(name);
        }
        else {
            lockerNetworkInterfaces[lockerSKU] = [name];
        }
    }
    return filteredLockers.map(filteredLocker => {
        const { locker_id: lockerID, sku } = filteredLocker;
        const locker = lockerID.replace(/[AF]/, '');
        const lockerName = locker.replace('L', `${t('LEVEL', {
            ns: 'Common'
        })} `);
        const lockerSKU = calculateDeviceId(sku, false);
        return Object.assign(Object.assign({}, filteredLocker), { alternateName: lockerName, crossConnections: lockerCrossConnections[lockerSKU], networkInterfaces: lockerNetworkInterfaces[lockerSKU] });
    });
}
export function calculateDeviceId(locker, convertToNetworking = true) {
    const lockerSKU = locker.split('.');
    const siteId = lockerSKU[3].replace('-', '.');
    if (convertToNetworking) {
        const lockerId = lockerSKU[0].replace('A', 'B');
        return `${lockerId}.${lockerSKU[1]}.${lockerSKU[2]}.${siteId}`;
    }
    return `${lockerSKU[0]}.${lockerSKU[1]}.${lockerSKU[2]}.${siteId}`;
}
